import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom'
import AppLayout from '../components/layout/app-layout'
import Login from '../features/auth/login.page'
import CompanySignupPage from '../features/auth/company-signup.page'
import SetupPassword from '../features/auth/setup-password.page'
import ForgotPassword from '../features/auth/forgot-password.page'
import ResetPassword from '../features/auth/reset-password.page'
// ProtectedRoute is no longer used - AuthWrapper handles auth in router.tsx
import { ErrorBoundary } from '../components/error-boundary'
import { ErrorPage } from '../components/error-page'
import { NotFound } from '../components/not-found'
import { FEATURES } from '../config/features.config'

// Superadmin pages
import CompaniesOverview from '../features/companies/companies-overview.page'
import CompanyForm from '../features/companies/company-form.page'
import CompanyUsers from '../features/companies/company-users.page'
import CompanyCrmPage from '../features/companies/company-crm.page'

// Admin pages
import AdminDashboard from '../features/admin/admin-dashboard.page'
// import SettingsPage from '../features/admin/settings.page'
import AbsenceRequestsPage from '../features/admin/absence-requests.page'
import BulletinBoardPage from '../features/admin/bulletin-board.page'
import TimeTrackingPage from '../features/admin/time-tracking.page'
import SubscriptionPage from '../features/admin/subscription.page'
import SubscriptionSuccessPage from '../features/admin/subscription-success.page'
import SubscriptionCancelPage from '../features/admin/subscription-cancel.page'
import SuperadminBillingOverviewPage from '../features/admin/superadmin-billing-overview.page'

// User pages
import UsersListPage from '../features/users/users-list.page'
import CreateUserPage from '../features/users/create-user.page'
import EditUserPage from '../features/users/edit-user.page'
import CreateEmployeeWizardPage from '../features/users/create-employee-wizard.page'

// Module pages
import ModulesList from '../features/modules/list.page'
import ModuleForm from '../features/modules/module-form.page'
import ModuleAssignmentPage from '../features/modules/module-assignment.page'

// Weiterbildung pages
import WeiterbildungList from '../features/weiterbildung/list.page'
import WeiterbildungFormPage from '../features/weiterbildung/weiterbildung-form.page'

// User profile pages
import UserProfile from '../features/user/profile.page'
import { AuthWrapper, RoleBasedDashboard, RoleBasedModuleView } from './router-wrappers'
// Employee detail mask (Personnel File)
import UserDetailLayout from '../features/users/user-detail.layout'
import EmployeePersonnelFileSection from '../features/users/sections/employee-personnel-file.section'
import EmployeePersonalInformationSection from '../features/users/sections/employee-personal-information.section'
import EmployeeAddressSection from '../features/users/sections/employee-address.section'
import EmployeeContactSection from '../features/users/sections/employee-contact.section'
import EmployeeDocumentsSection from '../features/users/sections/employee-documents.section'
import EmployeeTimeTrackingSection from '../features/users/sections/employee-time-tracking.section'
import EmployeeChatSection from '../features/users/sections/employee-chat.section'
import ChatPage from '../features/chat/chat.page'

export const routes: RouteObject[] = [
  {
    path: '/login',
    element: (
      <ErrorBoundary>
        <Login />
      </ErrorBoundary>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/signup',
    element: (
      <ErrorBoundary>
        <CompanySignupPage />
      </ErrorBoundary>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/auth/setup-password',
    element: (
      <ErrorBoundary>
        <SetupPassword />
      </ErrorBoundary>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/auth/forgot-password',
    element: (
      <ErrorBoundary>
        <ForgotPassword />
      </ErrorBoundary>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/auth/reset-password',
    element: (
      <ErrorBoundary>
        <ResetPassword />
      </ErrorBoundary>
    ),
    errorElement: <ErrorPage />,
  },
  {
    element: (
      <ErrorBoundary>
        <AppLayout />
      </ErrorBoundary>
    ),
    errorElement: <ErrorPage />,
    children: [
      // Role-based dashboard routing
      {
        index: true,
        element: (
          <AuthWrapper>
            <RoleBasedDashboard />
          </AuthWrapper>
        ),
      },

      // Superadmin routes
      {
        path: 'companies',
        element: (
          <AuthWrapper allow={['SUPERADMIN']}>
            <CompaniesOverview />
          </AuthWrapper>
        ),
      },
      {
        path: 'companies/new',
        element: (
          <AuthWrapper allow={['SUPERADMIN']}>
            <CompanyForm />
          </AuthWrapper>
        ),
      },
      {
        path: 'companies/:id/edit',
        element: (
          <AuthWrapper allow={['SUPERADMIN']}>
            <CompanyForm />
          </AuthWrapper>
        ),
      },
      {
        path: 'companies/:id/crm',
        element: (
          <AuthWrapper allow={['SUPERADMIN']}>
            <CompanyCrmPage />
          </AuthWrapper>
        ),
      },
      {
        path: 'companies/:id/users',
        element: (
          <AuthWrapper allow={['SUPERADMIN']}>
            <CompanyUsers />
          </AuthWrapper>
        ),
      },
      {
        path: 'companies/:id/users/invite',
        element: (
          <AuthWrapper allow={['SUPERADMIN']}>
            <CreateUserPage />
          </AuthWrapper>
        ),
      },
      {
        path: 'billing-overview',
        element: (
          <AuthWrapper allow={['SUPERADMIN']}>
            <SuperadminBillingOverviewPage />
          </AuthWrapper>
        ),
      },

      // Admin routes
      {
        path: 'admin',
        element: (
          <AuthWrapper allow={['ADMIN']}>
            <AdminDashboard />
          </AuthWrapper>
        ),
      },
      // {
      //   path: 'settings',
      //   element: (
      //     <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
      //       <SettingsPage />
      //     </AuthWrapper>
      //   ),
      // },
      {
        path: 'absence-requests',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <AbsenceRequestsPage />
          </AuthWrapper>
        ),
      },
      {
        path: 'bulletin-board',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <BulletinBoardPage />
          </AuthWrapper>
        ),
      },
      ...(FEATURES.timeTracking
        ? [
            {
              path: 'time-tracking',
              element: (
                <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
                  <TimeTrackingPage />
                </AuthWrapper>
              ),
            },
          ]
        : []),
      {
        path: 'chat',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <ChatPage />
          </AuthWrapper>
        ),
      },
      {
        path: 'subscription',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <SubscriptionPage />
          </AuthWrapper>
        ),
      },
      {
        path: 'subscription/success',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <SubscriptionSuccessPage />
          </AuthWrapper>
        ),
      },
      {
        path: 'subscription/cancel',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <SubscriptionCancelPage />
          </AuthWrapper>
        ),
      },

      // User management routes (accessible by admin and superadmin)
      {
        path: 'users',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <UsersListPage />
          </AuthWrapper>
        ),
      },
      {
        path: 'users/new',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <CreateEmployeeWizardPage />
          </AuthWrapper>
        ),
      },
      {
        path: 'users/:id/edit',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <EditUserPage />
          </AuthWrapper>
        ),
      },
      {
        path: 'users/:id',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <UserDetailLayout />
          </AuthWrapper>
        ),
        children: [
          { index: true, element: <Navigate to='personnel-file' replace /> },
          { path: 'personnel-file', element: <EmployeePersonnelFileSection /> },
          { path: 'personal-information', element: <EmployeePersonalInformationSection /> },
          { path: 'address', element: <EmployeeAddressSection /> },
          { path: 'contact', element: <EmployeeContactSection /> },
          { path: 'documents', element: <EmployeeDocumentsSection /> },
          ...(FEATURES.timeTracking
            ? [{ path: 'time-tracking', element: <EmployeeTimeTrackingSection /> }]
            : [{ path: 'time-tracking', element: <Navigate to='personnel-file' replace /> }]),
          { path: 'chat', element: <EmployeeChatSection /> },
        ],
      },

      // Profile route (accessible by all authenticated users)
      {
        path: 'profile',
        element: (
          <AuthWrapper>
            <UserProfile />
          </AuthWrapper>
        ),
      },

      // Module routes (accessible by all roles)
      {
        path: 'modules',
        element: (
          <AuthWrapper>
            <ErrorBoundary>
              <ModulesList />
            </ErrorBoundary>
          </AuthWrapper>
        ),
      },
      {
        path: 'modules/new',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <ErrorBoundary>
              <ModuleForm />
            </ErrorBoundary>
          </AuthWrapper>
        ),
      },
      {
        path: 'modules/:id/edit',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <ErrorBoundary>
              <ModuleForm />
            </ErrorBoundary>
          </AuthWrapper>
        ),
      },
      {
        path: 'modules/:id/assign',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <ErrorBoundary>
              <ModuleAssignmentPage />
            </ErrorBoundary>
          </AuthWrapper>
        ),
      },
      {
        path: 'modules/:id',
        element: (
          <AuthWrapper>
            <ErrorBoundary>
              <RoleBasedModuleView />
            </ErrorBoundary>
          </AuthWrapper>
        ),
        errorElement: <ErrorPage />,
      },

      // Weiterbildung routes
      {
        path: 'weiterbildung',
        element: (
          <AuthWrapper>
            <ErrorBoundary>
              <WeiterbildungList />
            </ErrorBoundary>
          </AuthWrapper>
        ),
      },
      {
        path: 'weiterbildung/new',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <ErrorBoundary>
              <WeiterbildungFormPage />
            </ErrorBoundary>
          </AuthWrapper>
        ),
      },
      {
        path: 'weiterbildung/:id/edit',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <ErrorBoundary>
              <WeiterbildungFormPage />
            </ErrorBoundary>
          </AuthWrapper>
        ),
      },
      {
        path: 'weiterbildung/:id/assign',
        element: (
          <AuthWrapper allow={['ADMIN', 'SUPERADMIN']}>
            <ErrorBoundary>
              <ModuleAssignmentPage />
            </ErrorBoundary>
          </AuthWrapper>
        ),
      },
      {
        path: 'weiterbildung/:id',
        element: (
          <AuthWrapper>
            <ErrorBoundary>
              <RoleBasedModuleView />
            </ErrorBoundary>
          </AuthWrapper>
        ),
        errorElement: <ErrorPage />,
      },
    ],
  },
  // Catch-all route for 404 errors
  {
    path: '*',
    element: <NotFound />,
  },
]

export const router = createBrowserRouter(routes)
